import { useMemo } from 'react'
import FilterSelect from './FilterSelect'
import { getFilterOptions } from '../../Utils/Product_filter_util'
import { dogFilterCategories } from './dogFilterCategories'
import { catFilterCategories } from './catFilterCategories'

const FILTER_CATEGORY_MAP = {
  dog: dogFilterCategories,
  cat: catFilterCategories,
}

const SORT_OPTIONS = [
  { value: 'name-asc', label: 'Name (A-Z)' },
  { value: 'name-desc', label: 'Name (Z-A)' },
  { value: 'price-asc', label: 'Price (Low to High)' },
  { value: 'price-desc', label: 'Price (High to Low)' },
]

const CLEAR_COLORS = {
  violet: 'border-violet-300 text-violet-600 hover:bg-violet-50',
  blue: 'border-blue-300 text-blue-600 hover:bg-blue-50',
  purple: 'border-purple-300 text-purple-600 hover:bg-purple-50',
  indigo: 'border-indigo-300 text-indigo-600 hover:bg-indigo-50',
  emerald: 'border-emerald-300 text-emerald-600 hover:bg-emerald-50',
  red: 'border-red-300 text-red-600 hover:bg-red-50',
}

const SEARCH_FOCUS = {
  violet: 'focus:border-violet-300 focus:ring-2 focus:ring-violet-200',
  blue: 'focus:border-blue-300 focus:ring-2 focus:ring-blue-200',
  purple: 'focus:border-purple-300 focus:ring-2 focus:ring-purple-200',
  indigo: 'focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200',
  emerald: 'focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200',
  red: 'focus:border-red-300 focus:ring-2 focus:ring-red-200',
}

export default function FilterBar({
  filters = [],
  // Decentralised category system - pages select correct category
  categories,
  filterCategories,
  categoryType,
  filterType,
  type,
  products,
  searchParams,
  setParam,
  sortBy,
  onSortChange,
  searchTerm,
  onSearchChange,
  searchPlaceholder = 'Search...',
  onClear,
  activeFilterCount = 0,
  resultCount,
  totalCount,
  resultUnit = 'products',
  colorScheme = 'violet',
}) {
  const clearCls = CLEAR_COLORS[colorScheme] || CLEAR_COLORS.violet
  const searchFocus = SEARCH_FOCUS[colorScheme] || SEARCH_FOCUS.violet

  // Resolve decentralised categories: pages import and pass categories array,
  // or pass a type string like "dog"/"cat" to select from FILTER_CATEGORY_MAP
  const resolvedCategories = useMemo(() => {
    if (categories) return categories
    if (filterCategories) return filterCategories
    const typeKey = categoryType || filterType || type
    if (typeKey && FILTER_CATEGORY_MAP[typeKey]) return FILTER_CATEGORY_MAP[typeKey]
    return null
  }, [categories, filterCategories, categoryType, filterType, type])

  const formatSizeLabel = (value) => {
    if (!value || value === 'All') return value
    // Title Case each word: "very small" -> "Very Small", "small" -> "Small"
    return String(value)
      .split(' ')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(' ')
  }

  const generatedFilters = useMemo(() => {
    if (!resolvedCategories || !products) return null
    // products + searchParams + setParam must be provided for auto-generation
    if (!searchParams || !setParam) return null
    return resolvedCategories.map((cat) => {
      const rawOptions = getFilterOptions(products, cat.key)
      // For size, display Title Case but keep lower-case value for filtering
      let options = rawOptions
      if (cat.key === 'size') {
        options = rawOptions.map((opt) => {
          if (opt === 'All') return opt
          return { value: opt, label: formatSizeLabel(opt) }
        })
      }
      // For brand, ensure normal capitalization (already "Royal Canin"/"Eukanuba")
      if (cat.key === 'brand' || cat.key === 'brands') {
        options = rawOptions.map((opt) => {
          if (opt === 'All') return opt
          return String(opt)
        })
      }
      return {
        key: cat.key,
        label: cat.label,
        options,
        value: searchParams.get(cat.key) || 'All',
        onChange: (v) => setParam(cat.key, v),
      }
    })
  }, [resolvedCategories, products, searchParams, setParam])

  const effectiveFilters = generatedFilters || filters

  const effectiveActiveCount = useMemo(() => {
    if (generatedFilters) {
      return generatedFilters.filter((f) => f.value !== 'All').length
    }
    return activeFilterCount
  }, [generatedFilters, activeFilterCount])

  return (
    <div className="mb-8 flex flex-col gap-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap">
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-gray-700">Search</span>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={searchPlaceholder}
            className={`w-64 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none transition ${searchFocus}`}
          />
        </label>

        {effectiveFilters.map((f) => (
          <FilterSelect
            key={f.key}
            label={f.label}
            value={f.value}
            options={f.options}
            onChange={f.onChange}
            colorScheme={colorScheme}
          />
        ))}

        <FilterSelect
          label="Sort by"
          value={sortBy}
          options={SORT_OPTIONS}
          onChange={onSortChange}
          colorScheme={colorScheme}
        />

        {effectiveActiveCount > 0 && (
          <button
            onClick={onClear}
            className={`self-end rounded-lg border bg-white px-4 py-2 text-sm font-medium transition ${clearCls}`}
          >
            Clear filters
          </button>
        )}
      </div>

      <p className="text-sm text-gray-500">
        {resultCount === 0
          ? 'No products match your filters'
          : `Showing ${resultCount} of ${totalCount} ${resultUnit}`
        }
      </p>
    </div>
  )
}
